"use client";

import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import RichText from "@/components/RichText";
import { LogoMarqueeRow } from "@/components/pro-blocks/landing-page/logo-sections/logo-marquee-row";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";

type LogoItem = {
  src: string;
  alt?: string;
  title?: string;
  multiplier?: number;
  objectFit?: MediaObjectFit;
};

type LogoSectionProps = {
  tagline?: string;
  title?: string;
  subtitle?: string;
  titleTag?: "h1" | "h2" | "h3";
  titleClassName?: string;
  richtext?: DefaultTypedEditorState;
  speed?: number;
  direction?: "left" | "right";
  logos: LogoItem[];
};

export function LogoSection({
  tagline,
  title,
  subtitle,
  titleTag = "h2",
  titleClassName = "heading-lg",
  richtext,
  speed,
  direction = "left",
  logos,
}: LogoSectionProps) {
  const TitleTag = titleTag;
  if (!logos.length) return null;

  return (
    <section className="bg-background section-padding-y">
      <div className="md:container-padding-x md:container mx-auto">
        <div className="flex flex-col items-center gap-12 md:gap-16">
          {tagline || title || subtitle ? (
            <div className="section-title-gap-lg flex w-full lg:max-w-lg 3xl:max-w-3xl flex-col items-center text-center">
              {tagline ? <Tagline>{tagline}</Tagline> : null}
              {title ? (
                <TitleTag
                  className={`${titleClassName} text-foreground w-full text-balance`}
                >
                  {title}
                </TitleTag>
              ) : null}
              {subtitle ? (
                <p className={`${titleClassName} text-muted-foreground w-full`}>
                  {subtitle}
                </p>
              ) : null}
              {richtext ? (
                <RichText
                  data={richtext}
                  enableGutter={false}
                  enableProse={false}
                  className="text-muted-foreground text-base text-pretty"
                />
              ) : null}
            </div>
          ) : null}

          <LogoMarqueeRow
            rowId="logo-section"
            direction={direction}
            speed={speed}
            logos={logos}
          />
        </div>
      </div>
    </section>
  );
}

LogoSection.displayName = "Logo section";

export { LogoSection as LogoSection10 };
